import { ADVERTISING, isAllowed } from './consent';

/**
 * Meta Pixel, consent-gated.
 *
 * Scope is deliberately narrow, and the privacy policy (fourth edit, October 2026) describes
 * exactly this, so widening it means changing the policy first:
 *
 * - Marketing site only. The member app never loads it.
 * - Nothing loads, and no request goes to Meta, until the visitor turns Advertising on.
 * - No advanced matching: `init` is called with the pixel ID alone, never an email or phone.
 * - No automatic event setup (`autoConfig` off), so Meta does not collect button text or page
 *   metadata on its own. Only the events below are sent.
 * - No Conversions API. Signups are counted from our own UTM record in the app, not by Meta.
 *
 * The ID is the "CareersKitchen Web" dataset in the CareersKitchen business account. A pixel ID
 * is public (it appears in every page that loads the pixel), so it lives here rather than in a
 * secret. It only applies on the production host, so staging, review apps and local builds never
 * report into the live dataset. GATSBY_META_PIXEL_ID overrides it on any host (for a test
 * dataset); with neither, the whole module is a no-op.
 *
 * The 2021 Event Setup Tool rules on this dataset (button-text Purchase / InitiateCheckout /
 * StartTrial / ViewContent) were set INACTIVE in October 2026. Keep them off: they would send
 * events the privacy policy does not describe.
 */
const PRODUCTION_PIXEL_ID = '706963403298810';
const PRODUCTION_HOST = 'www.careerskitchen.com';

const isBrowser = () => typeof window !== 'undefined';

const resolvePixelId = () => {
  if (process.env.GATSBY_META_PIXEL_ID) return process.env.GATSBY_META_PIXEL_ID;
  if (isBrowser() && window.location.hostname === PRODUCTION_HOST) return PRODUCTION_PIXEL_ID;
  return '';
};

let PIXEL_ID = null;
// Resolved on first browser use, never during SSR (no window, so it would cache an empty ID).
const pixelId = () => {
  if (!isBrowser()) return '';
  if (PIXEL_ID === null) PIXEL_ID = resolvePixelId();
  return PIXEL_ID;
};

let loaded = false;
let granted = false;
let lastPageViewPath = null;
let pending = [];
// Once-per-page events already sent or queued, keyed by event name, for the current path only.
let oncePath = null;
let onceSent = new Set();

export const isConfigured = () => Boolean(pixelId());

const canSend = () =>
  isBrowser() && loaded && typeof window.fbq === 'function' && isAllowed(ADVERTISING);

/* Meta's standard base snippet, unminified enough to read. It defines a queueing `fbq` stub and
   appends fbevents.js asynchronously. */
const injectBaseCode = () => {
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
};

export const trackPageView = (pathname) => {
  if (!canSend()) return;

  // Gatsby fires onRouteUpdate on first render as well as on navigation, and the pixel can load
  // part-way through a page. Without this the same page view could be counted twice.
  if (pathname === lastPageViewPath) return;
  lastPageViewPath = pathname;

  window.fbq('track', 'PageView');
};

/**
 * Sends a standard event once per page view: now, or once the pixel loads if the visitor
 * consents while still on the page that asked for it. Queued events are dropped on navigation, so a consent given three
 * pages later never back-fills a view the visitor made before agreeing.
 */
export const trackWhenReady = (event, params = {}) => {
  if (!isBrowser() || !isConfigured()) return;

  // The page can mount more than once for one view (the region provider re-renders the tree
  // once the visitor's region resolves), so this is once per event per page, not per mount.
  const here = window.location.pathname;
  if (here !== oncePath) {
    oncePath = here;
    onceSent = new Set();
  }
  if (onceSent.has(event)) return;
  onceSent.add(event);

  if (canSend()) {
    window.fbq('track', event, params);
    return;
  }

  pending.push({ event, params, pathname: window.location.pathname });
};

export const trackCustom = (event, params = {}) => {
  if (!canSend()) return;

  window.fbq('trackCustom', event, params);
};

const flushPending = () => {
  const here = window.location.pathname;
  const queued = pending.filter((item) => item.pathname === here);
  pending = [];
  queued.forEach(({ event, params }) => window.fbq('track', event, params));
};

/**
 * Called on mount and whenever consent changes. Loads on the first grant, grants and revokes
 * thereafter. A script cannot be unloaded, so after withdrawal it is told to stop via Meta's own
 * consent API, and the consent module sweeps `_fbp`/`_fbc`.
 */
export const syncPixelWithConsent = () => {
  if (!isBrowser() || !isConfigured()) return;

  const allowed = isAllowed(ADVERTISING);

  // Consent changes for other categories (analytics, chat) land here too; only act on a change
  // to this one, or toggling chat would re-send a page view.
  if (allowed === granted) return;
  granted = allowed;

  if (!allowed) {
    if (loaded && typeof window.fbq === 'function') window.fbq('consent', 'revoke');
    pending = [];
    return;
  }

  if (!loaded) {
    injectBaseCode();
    window.fbq('set', 'autoConfig', false, pixelId());
    window.fbq('init', pixelId());
    loaded = true;
  } else {
    window.fbq('consent', 'grant');
  }

  lastPageViewPath = null;
  trackPageView(window.location.pathname);
  flushPending();
};
